import { isDevStage, unixMsDate, utcUnixMs } from "@dashboard-config"
import type { AuthUser, DashboardRole } from "@playbrain/auth/dashboardRoles"
import { hasDashboardRole } from "@playbrain/auth/dashboardRoles"

export const DASHBOARD_SESSION_COOKIE = "playbrain_dashboard_session"
export const DASHBOARD_SESSION_IDLE_MS = 30 * 60 * 1000
export const DASHBOARD_SESSION_TTL_MS = 60 * 60 * 1000
export const DASHBOARD_SESSION_ABSOLUTE_MS = 12 * 60 * 60 * 1000

type DashboardSessionClaims = {
  user: AuthUser
  createdAt: number
  lastSeenAt: number
  expiresAt: number
}

export type DashboardSession = DashboardSessionClaims & {
  token: string
}

export type DashboardAuthResult = { user: AuthUser; session: DashboardSession } | { response: Response }

export async function createDashboardSessionCookie(user: AuthUser, now = utcUnixMs()) {
  const claims = createDashboardSessionClaims(user, now)
  const token = await signDashboardSession(claims)
  return serializeDashboardSessionCookie(token, claims.expiresAt)
}

export async function readDashboardSession(request: Request, now = utcUnixMs()) {
  const cookie = parseCookieHeader(request.headers.get("cookie"))
  const token = cookie[DASHBOARD_SESSION_COOKIE]
  if (!token) return null

  const claims = await verifyDashboardSession(token)
  if (!claims) return null
  if (claims.expiresAt <= now) return null
  if (claims.lastSeenAt + DASHBOARD_SESSION_IDLE_MS <= now) return null
  if (claims.createdAt + DASHBOARD_SESSION_ABSOLUTE_MS <= now) return null

  return { ...claims, token } satisfies DashboardSession
}

export async function refreshDashboardSessionCookie(session: DashboardSession, now = utcUnixMs()) {
  const absoluteExpiresAt = session.createdAt + DASHBOARD_SESSION_ABSOLUTE_MS
  const expiresAt = Math.min(now + DASHBOARD_SESSION_TTL_MS, absoluteExpiresAt)
  const claims: DashboardSessionClaims = {
    user: session.user,
    createdAt: session.createdAt,
    lastSeenAt: now,
    expiresAt,
  }
  const token = await signDashboardSession(claims)
  return serializeDashboardSessionCookie(token, expiresAt)
}

export function clearDashboardSessionCookie() {
  return serializeCookie(DASHBOARD_SESSION_COOKIE, "", {
    maxAge: 0,
    expires: unixMsDate(0),
    path: "/",
    httpOnly: true,
    secure: shouldUseSecureCookies(),
    sameSite: "Lax",
  })
}

export async function requireDashboardCookieAuth(
  request: Request,
  roles: DashboardRole[] = ["admin", "editor", "viewer"]
): Promise<DashboardAuthResult> {
  const session = await readDashboardSession(request)
  if (!session) {
    if (allowLocalDevBypass()) {
      const now = utcUnixMs()
      return {
        user: {
          sub: "local-dev",
          email: "local-dev@playbrain.com",
          name: "Local Dev",
          user_metadata: { roles: ["admin"] },
        },
        session: {
          token: "local-dev",
          user: {
            sub: "local-dev",
            email: "local-dev@playbrain.com",
            name: "Local Dev",
            user_metadata: { roles: ["admin"] },
          },
          createdAt: now,
          lastSeenAt: now,
          expiresAt: now + DASHBOARD_SESSION_TTL_MS,
        },
      }
    }
    return { response: Response.json({ error: "Missing dashboard session" }, { status: 401 }) }
  }

  if (!hasDashboardRole(session.user, roles)) {
    return { response: Response.json({ error: "Insufficient dashboard role" }, { status: 403 }) }
  }

  return { user: session.user, session }
}

export function parseCookieHeader(header: string | null): Record<string, string> {
  const cookies: Record<string, string> = {}
  if (!header) return cookies
  for (const part of header.split(";")) {
    const [name, ...valueParts] = part.trim().split("=")
    if (!name) continue
    cookies[name] = decodeURIComponent(valueParts.join("="))
  }
  return cookies
}

export function dashboardSessionPublicPayload(session: DashboardSession) {
  return {
    user: session.user,
    expiresAt: session.expiresAt,
    idleExpiresAt: session.lastSeenAt + DASHBOARD_SESSION_IDLE_MS,
    absoluteExpiresAt: session.createdAt + DASHBOARD_SESSION_ABSOLUTE_MS,
  }
}

function createDashboardSessionClaims(user: AuthUser, now: number): DashboardSessionClaims {
  return {
    user,
    createdAt: now,
    lastSeenAt: now,
    expiresAt: now + DASHBOARD_SESSION_TTL_MS,
  }
}

async function signDashboardSession(claims: DashboardSessionClaims) {
  const payload = base64UrlEncode(JSON.stringify(claims))
  const signature = await signPayload(payload)
  return `${payload}.${signature}`
}

async function verifyDashboardSession(token: string): Promise<DashboardSessionClaims | null> {
  try {
    const [payloadValue, signature] = token.split(".")
    if (!payloadValue || !signature || !(await verifyPayloadSignature(payloadValue, signature))) return null
    const payload = JSON.parse(base64UrlDecode(payloadValue)) as Partial<DashboardSessionClaims>
    if (!payload.user || !payload.createdAt || !payload.lastSeenAt || !payload.expiresAt) return null
    return {
      user: payload.user,
      createdAt: payload.createdAt,
      lastSeenAt: payload.lastSeenAt,
      expiresAt: payload.expiresAt,
    }
  } catch {
    return null
  }
}

function sessionSecret() {
  const secret =
    process.env.AUTH_SESSION_SECRET ||
    process.env.AUTH0_CLIENT_SECRET ||
    (allowLocalDevBypass() ? "local-dashboard-session-secret" : "")
  if (!secret) throw new Error("AUTH_SESSION_SECRET is required for dashboard cookie sessions.")
  return secret
}

async function signPayload(payload: string) {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(sessionSecret()),
    { hash: "SHA-256", name: "HMAC" },
    false,
    ["sign"]
  )
  const signature = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(payload))
  return bytesToBase64Url(new Uint8Array(signature))
}

async function verifyPayloadSignature(payload: string, signature: string) {
  const expected = await signPayload(payload)
  if (expected.length !== signature.length) return false
  let difference = 0
  for (let index = 0; index < expected.length; index++) {
    difference |= expected.charCodeAt(index) ^ signature.charCodeAt(index)
  }
  return difference === 0
}

function base64UrlEncode(value: string) {
  return Buffer.from(value, "utf8").toString("base64url")
}

function base64UrlDecode(value: string) {
  return Buffer.from(value, "base64url").toString("utf8")
}

function bytesToBase64Url(bytes: Uint8Array) {
  return Buffer.from(bytes).toString("base64url")
}

function serializeDashboardSessionCookie(token: string, expiresAt: number) {
  return serializeCookie(DASHBOARD_SESSION_COOKIE, token, {
    expires: unixMsDate(expiresAt),
    maxAge: Math.max(0, Math.floor((expiresAt - utcUnixMs()) / 1000)),
    path: "/",
    httpOnly: true,
    secure: shouldUseSecureCookies(),
    sameSite: "Lax",
  })
}

function serializeCookie(
  name: string,
  value: string,
  options: {
    expires?: Date
    httpOnly?: boolean
    maxAge?: number
    path?: string
    sameSite?: "Lax" | "Strict" | "None"
    secure?: boolean
  }
) {
  const parts = [`${name}=${encodeURIComponent(value)}`]
  if (options.maxAge !== undefined) parts.push(`Max-Age=${options.maxAge}`)
  if (options.expires) parts.push(`Expires=${options.expires.toUTCString()}`)
  if (options.path) parts.push(`Path=${options.path}`)
  if (options.httpOnly) parts.push("HttpOnly")
  if (options.secure) parts.push("Secure")
  if (options.sameSite) parts.push(`SameSite=${options.sameSite}`)
  return parts.join("; ")
}

function shouldUseSecureCookies() {
  const stage = process.env.PUBLIC_STAGE || process.env.VITE_PUBLIC_STAGE
  const domain = process.env.DOMAIN || ""
  if (domain.includes("localhost") || domain.includes("127.0.0.1")) return false
  return stage !== "local" && stage !== undefined
}

function allowLocalDevBypass() {
  return (
    isDevStage(process.env.PUBLIC_STAGE) &&
    readBooleanEnv(process.env.DASHBOARD_AUTH_BYPASS) &&
    !process.env.AUTH0_DOMAIN &&
    !process.env.AUTH0_CLIENT_ID
  )
}

function readBooleanEnv(value: string | undefined) {
  if (!value) return false
  return ["1", "true", "yes", "on"].includes(value.toLowerCase())
}
